import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Link, useParams } from "react-router-dom";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { GraphCanvas } from "../components/graph/GraphCanvas";
import { Legend } from "../components/graph/Legend";
import { SpecDrawer } from "../components/spec/SpecDrawer";
import { useDecisions, useQuestions } from "../hooks/useQueues";
import { useSpec } from "../hooks/useSpec";
import { useSpecFollow } from "../hooks/useSpecFollow";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { getDisplayName } from "../lib/displayName";
import { PAGE_LOAD_ERROR_LINE, TRY_AGAIN_ACT } from "../lib/nextStep";
import {
  IDEA_NOT_FOUND_LINE,
  MAP_CRUMB,
  MAP_EMPTY_LINE,
  MAP_NARROW_LINE,
  isMapIdea,
  mapDataOf,
  specReadNotFound,
} from "../lib/specLines";

// The map at /products/:pid/graph (0051 slice 3): the optional map (0051's
// ruling 3: kept as an optional map view, not the primary way to read the
// spec) over the spec read, never /tree. It draws the current decisions and
// open items around the idea (0171 Open question 1, option (a)) and only the
// selected node's links; selecting a node opens the same drawer the spec
// column opens. It follows the served next step as the product page does
// (hooks/useSpecFollow), so a pass that ends in another tab or device redraws
// it too. Below 600 px it says to open it on a wider screen and mounts no
// canvas.
export function Graph() {
  const { productId } = useParams<{ productId: string }>();
  const { data: user } = useUser();
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const spec = useSpec(productId!);
  const map = useMemo(() => mapDataOf(spec.data), [spec.data]);

  // The queues the drawer's "Review it →" opens at the item's place.
  const questions = useQuestions(productId!);
  const decisions = useDecisions(productId!);

  // The product page's one rule (hooks/useSpecFollow): when the served step's
  // latest pass or state changes, the spec read and the queues refetch; the
  // drawer's in-flight signal (the store's live run, or the served step
  // working) holds Amend.
  const { inFlight } = useSpecFollow(productId ?? "");

  const wide = useMapFits();

  // The idea at the centre selects nothing (it has no row to open).
  const selectNode = useCallback(
    (id: string | null) => {
      if (id !== null && map.nodes.some((n) => n.id === id && isMapIdea(n))) return;
      setSelectedNodeId(id);
    },
    [map],
  );
  const closeDrawer = useCallback(() => setSelectedNodeId(null), []);

  // The drawer is open while the selection names a row of the read. A
  // selection whose row leaves the read (a pass settled the question or took
  // up the saved change) clears, so the map never stays selected with no
  // drawer, and the row coming back later opens nothing.
  const drawerOpen =
    selectedNodeId !== null && (spec.data?.rows.some((r) => r.node.id === selectedNodeId) ?? false);
  useEffect(() => {
    if (selectedNodeId !== null && !drawerOpen) setSelectedNodeId(null);
  }, [selectedNodeId, drawerOpen]);

  const displayName = spec.data ? getDisplayName(spec.data.meta) : null;
  useDocumentTitle(displayName);

  if (!user) return null;

  const ideaText = spec.data?.meta.idea ?? "";
  const namePreview = displayName ? truncate(displayName, 40) : "loading…";

  const breadcrumb = (
    <>
      <span className="text-ink-600">/</span>
      <Link to="/" className="font-mono text-ink-400 hover:text-ink-200">
        ideas
      </Link>
      <span className="text-ink-600">/</span>
      <Link
        to={`/products/${productId}`}
        className="font-mono text-ink-300 hover:text-ink-100 truncate max-w-[28ch]"
        title={ideaText}
      >
        {namePreview}
      </Link>
      <span className="text-ink-600">/</span>
      <span className="font-mono text-ink-300">{MAP_CRUMB}</span>
    </>
  );

  const backToSpec = (
    <Link
      to={`/products/${productId}`}
      className="mt-6 inline-block text-sm text-accent hover:text-accent-glow"
    >
      ← back to spec
    </Link>
  );

  if (spec.isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} breadcrumb={breadcrumb} />
        <main className="flex-1 flex items-center justify-center text-sm text-ink-400 font-mono">
          loading…
        </main>
      </div>
    );
  }

  if (spec.error) {
    // A missing idea reads as on the product page (specReadNotFound, its
    // rule): it never loads, so no Try again, and the way back is the ideas.
    if (specReadNotFound(spec.error)) {
      return (
        <div className="min-h-screen flex flex-col">
          <Header user={user} />
          <main className="flex-1 flex items-center justify-center px-4">
            <div className="text-center">
              <p className="font-mono text-xs text-ink-400 mb-2">404</p>
              <p className="text-ink-300">{IDEA_NOT_FOUND_LINE}</p>
              <Link to="/" className="text-accent hover:text-accent-glow mt-4 inline-block">
                ← back to ideas
              </Link>
            </div>
          </main>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} breadcrumb={breadcrumb} />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            <p className="text-ink-300">{PAGE_LOAD_ERROR_LINE}</p>
            <div className="mt-3">
              <button
                type="button"
                onClick={() => void spec.refetch()}
                className="rounded-md border border-ink-700 px-3.5 py-1.5 text-xs text-ink-200 hover:border-ink-600 hover:text-ink-100 transition-colors"
              >
                {TRY_AGAIN_ACT}
              </button>
            </div>
            {backToSpec}
          </div>
        </main>
      </div>
    );
  }

  // Nothing to draw but the idea (or nothing at all).
  if (spec.data === undefined || map.stateOf.size === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header user={user} breadcrumb={breadcrumb} />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <p className="text-ink-200 leading-relaxed">{MAP_EMPTY_LINE}</p>
            {backToSpec}
          </div>
        </main>
      </div>
    );
  }

  const specData = spec.data;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Header user={user} breadcrumb={breadcrumb} />

      {/* Below 600 px the map does not work: the page says so, and the spec
          column is the phone's way to read the spec. */}
      <main
        data-testid="map-narrow"
        className="flex-1 hidden max-[599px]:flex items-center justify-center px-6"
      >
        <div className="text-center max-w-sm">
          <p className="text-ink-200 leading-relaxed">{MAP_NARROW_LINE}</p>
          {backToSpec}
        </div>
      </main>

      {/* The canvas mounts only from 600 px, so no layout or draw loop runs
          behind the narrow line on a phone. */}
      <main data-testid="map-canvas" className="flex-1 relative max-[599px]:hidden overflow-hidden">
        {wide && (
          <div className="absolute inset-0 bg-ink-950">
            <GraphCanvas
              map={map}
              selectedNodeId={drawerOpen ? selectedNodeId : null}
              onSelectNode={selectNode}
            />
            <Legend />
          </div>
        )}
      </main>

      {/* The same drawer the spec column opens, over the map. */}
      {drawerOpen && (
        <SpecDrawer
          productId={productId!}
          spec={specData}
          rowId={selectedNodeId}
          questions={questions.data}
          decisions={decisions.data}
          inFlight={inFlight}
          onOpenRow={setSelectedNodeId}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
}

// The map's narrow cut-off, the narrow line's (max-[599px] above): from it the
// canvas mounts; below it no canvas, no layout and no draw loop.
const MAP_FITS_QUERY = "(min-width: 600px)";

function mapFitsQuery(): MediaQueryList | null {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia(MAP_FITS_QUERY)
    : null;
}

function subscribeMapFits(onChange: () => void): () => void {
  const mql = mapFitsQuery();
  if (mql === null) return () => {};
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }
  // Older Safari.
  mql.addListener(onChange);
  return () => mql.removeListener(onChange);
}

// Where nothing answers the media query (no matchMedia), the canvas mounts.
function mapFitsNow(): boolean {
  return mapFitsQuery()?.matches ?? true;
}

// useMapFits: the viewport is at least the map's cut-off wide, followed live.
function useMapFits(): boolean {
  return useSyncExternalStore(subscribeMapFits, mapFitsNow, () => true);
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}
